import { useState } from 'react';
import { useNavigate } from 'react-router';
import { apiSend, useMe } from '../../api/client';
import { useLoginRedirect } from '../../components/LoginGate';

type Vote = -1 | 0 | 1;

/**
 * Up/down arrows with an optimistic score. Anonymous clicks route to login; a failed
 * request rolls the local state back and shows the error.
 */
export function VoteArrows({
  targetType,
  targetId,
  score,
  myVote,
  compact = false,
}: {
  targetType: 'artifact' | 'comment';
  targetId: string;
  score: number;
  myVote: Vote;
  compact?: boolean;
}) {
  const me = useMe();
  const navigate = useNavigate();
  const loginUrl = useLoginRedirect();
  const [state, setState] = useState({ score, myVote });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const user = me.data?.user ?? null;

  const cast = async (value: Vote) => {
    if (!user) {
      navigate(loginUrl);
      return;
    }
    if (user.banned) {
      setError('Your account is banned.');
      return;
    }
    const next: Vote = state.myVote === value ? 0 : value;
    const before = state;
    setState({ score: state.score - state.myVote + next, myVote: next });
    setBusy(true);
    setError(null);
    try {
      const res = await apiSend<{ score: number; myVote: Vote }>('PUT', '/api/votes', {
        targetType,
        targetId,
        value: next,
      });
      setState(res);
    } catch (err) {
      setState(before);
      setError((err as Error).message || 'Vote failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span
      className="vote"
      data-testid={`vote-${targetType}-${targetId}`}
      style={
        compact
          ? { display: 'inline-flex', flexDirection: 'row', width: 'auto', gap: 2 }
          : undefined
      }
    >
      <button
        type="button"
        className={`vote-arrow${state.myVote === 1 ? ' active' : ''}`}
        aria-label="Upvote"
        aria-pressed={state.myVote === 1}
        disabled={busy}
        onClick={() => cast(1)}
      >
        &#9650;
      </button>
      <span data-testid="score" aria-live="polite">
        {state.score}
      </span>
      <button
        type="button"
        className={`vote-arrow${state.myVote === -1 ? ' active' : ''}`}
        aria-label="Downvote"
        aria-pressed={state.myVote === -1}
        disabled={busy}
        onClick={() => cast(-1)}
      >
        &#9660;
      </button>
      {error && <span className="error">{error}</span>}
    </span>
  );
}
